<template>
  <div id="main-menu" class="d-flex flex-column h-100">
    <!-- LOGO SECTION -->
    <div class="menu-logo-container d-flex flex-column align-items-center justify-content-center py-4 mb-2">
      <div class="logo-wrapper" ref="logo">
        <b-img :src="$config.logo" class="main-logo menu-logo"></b-img>
      </div>
    </div>

    <!-- MENU SECTIONS -->
    <div class="menu-scroll-area flex-grow-1 pr-2">
      <div v-for="(section, sIndex) in visibleSections" :key="sIndex" class="menu-section mb-4" :ref="`section-${sIndex}`">
        <!-- HEADER -->
        <div
          v-if="section.title"
          @click="toggleSection(section.key)"
          class="section-header d-flex align-items-center mb-2 ml-3 cursor-pointer"
        >
          <h6 class="section-title text-muted text-uppercase mb-0 font-weight-bold" style="font-size: 0.7rem; letter-spacing: 1px;">
            {{ section.title }}
          </h6>
          <font-awesome-icon
            icon="chevron-down"
            class="ml-2 text-muted"
            style="font-size: 0.7rem; transition: transform 0.3s ease;"
            :style="{ transform: isCollapsed(section.key) ? 'rotate(-90deg)' : 'rotate(0deg)' }"
          />
        </div>

        <!-- ITEMS -->
        <div
          class="menu-items overflow-hidden"
          :ref="`section-items-${section.key}`"
          :style="{ height: isCollapsed(section.key) ? '0px' : 'auto', opacity: isCollapsed(section.key) ? 0 : 1 }"
        >
          <router-link
            v-for="(item, iIndex) in section.items"
            :key="iIndex"
            :to="item.path"
            class="menu-item d-flex align-items-center py-2 px-3 mb-1 text-decoration-none"
            active-class="active"
            v-slot="{ isExactActive, navigate, href }"
            custom
          >
            <a :href="href" @click="navigate" class="menu-link w-100 d-flex align-items-center" :class="{ 'active': isExactActive }">
              <div class="icon-wrapper menu-icon-wrapper d-flex justify-content-center align-items-center mr-3">
                <font-awesome-icon :icon="item.icon" :class="{ 'text-primary': isExactActive, 'text-muted': !isExactActive }" size="sm"/>
              </div>
              <span class="menu-text font-weight-medium" :class="{ 'text-white': isExactActive, 'text-muted-light': !isExactActive }">
                {{ item.label }}
              </span>
              <div v-if="isExactActive" class="active-indicator ml-auto"></div>
            </a>
          </router-link>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import config from "@/appConfig";
import { animate, stagger } from "motion"

export default {
  name: "Menu",

  data() {
    return {
      collapsedSections: {
        finance: true,
        system: true
      }
    }
  },

  computed: {
    config() {
      return config
    },
    userType() {
      return this.$store.state.session.user ? this.$store.state.session.user.type : null
    },

    // Define all sections and items, filtering by permissions
    visibleSections() {
      const type = this.userType;
      if (!type) return [];

      const sections = [];

      // 1. DASHBOARD
      const dashboard = { key: 'dashboard', title: "", items: [] };
      if (['editor', 'editor_in_chief', 'finance', 'admin', 'support'].includes(type)) {
        dashboard.items.push({ label: 'Home', path: '/', icon: 'home' });
      }
      if (dashboard.items.length > 0) sections.push(dashboard);

      // 2. CONTENT
      const content = { key: 'content', title: "Content", items: [] };
      if (['editor', 'editor_in_chief', 'admin'].includes(type)) {
        content.items.push({ label: 'Inbox', path: '/inbox', icon: 'inbox' });
        content.items.push({ label: 'Categories', path: '/categories', icon: 'layer-group' });
        content.items.push({ label: 'Media', path: '/media', icon: 'photo-video' });
      }
      if (this.config.enableDdex && ['editor', 'editor_in_chief', 'admin'].includes(type)) {
        content.items.push({ label: 'DDEX Import', path: '/ddex-import', icon: 'file-import' });
      }
      if (this.config.enableRssFeeds && ['editor', 'editor_in_chief', 'admin'].includes(type)) {
        content.items.push({ label: 'RSS Feeds', path: '/rss-feeds', icon: 'rss' });
      }
      if (content.items.length > 0) sections.push(content);

      // 3. OPERATIONS
      const operations = { key: 'operations', title: "Operations", items: [] };
      if (['support', 'admin'].includes(type)) {
        operations.items.push({ label: 'Users', path: '/users', icon: 'user-group' });
      }
      if (['finance', 'admin', 'editor', 'editor_in_chief'].includes(type)) {
         if (this.config.enableAffiliates) operations.items.push({ label: 'Affiliates', path: '/affiliates', icon: 'handshake' });
         if (this.config.enablePromoCodes) operations.items.push({ label: 'Promo Codes', path: '/promo-codes', icon: 'percent' });
         operations.items.push({ label: 'Partners', path: '/partners', icon: 'store' });
      }
      if (operations.items.length > 0) sections.push(operations);

      // 4. FINANCE
      const finance = { key: 'finance', title: "Finance", items: [] };
      if (['finance', 'admin'].includes(type)) {
        if (this.config.enablePpv) finance.items.push({ label: 'PPV Tiers', path: '/ppv-tiers', icon: 'funnel-dollar' });
        // TODO Fix later:
        finance.items.push({ label: 'Subscriptions', path: '/flex-subscriptions', icon: 'credit-card' });
        finance.items.push({ label: 'Currencies', path: '/currencies', icon: 'money-bill-wave' });
        finance.items.push({ label: 'Finance', path: '/finance', icon: 'chart-line' });
      }
      if (finance.items.length > 0) sections.push(finance);

      // 5. SYSTEM
      const system = { key: 'system', title: "System", items: [] };
      if (['finance', 'admin', 'editor', 'editor_in_chief'].includes(type)) {
        system.items.push({ label: 'Stats', path: '/stats', icon: 'chart-pie' });
      }
      if (system.items.length > 0) sections.push(system);

      return sections;
    }
  },

  mounted() {
    this.loadState()
    this.animateEntrance()
  },

  methods: {
    loadState() {
      try {
        const stored = localStorage.getItem('menu_collapsed_sections');
        if (stored) {
          const parsed = JSON.parse(stored);
          // Merge stored preferences with defaults
          this.collapsedSections = { ...this.collapsedSections, ...parsed };
        }
      } catch (e) {
        console.error("Failed to load menu state", e);
      }
    },

    toggleSection(sectionKey) {
      if (!sectionKey) return; // Skip dashboard or untitled sections

      const isCollapsed = this.collapsedSections[sectionKey];
      this.$set(this.collapsedSections, sectionKey, !isCollapsed);

      localStorage.setItem('menu_collapsed_sections', JSON.stringify(this.collapsedSections));

      // Animate the specific section
      const sectionEl = this.$refs[`section-items-${sectionKey}`][0];
      if (sectionEl) {
        if (!isCollapsed) {
          // Collapsing
          animate(sectionEl, { height: 0, opacity: 0 }, { duration: 0.3, easing: "ease-out" });
        } else {
          // Expanding
          animate(sectionEl, { height: "auto", opacity: 1 }, { duration: 0.3, easing: "spring()" });
        }
      }
    },

    isCollapsed(key) {
      return !!this.collapsedSections[key];
    },

    animateEntrance() {
      // Animate Logo
      animate(this.$refs.logo,
        { scale: [0.8, 1], opacity: [0, 1], y: [-20, 0] },
        { duration: 0.6, easing: "spring()" }
      )

      // Animate Menu Items - only those not collapsed
      const items = document.querySelectorAll('.menu-item');
      if (items.length > 0) {
        animate(items,
          { x: [-20, 0], opacity: [0, 1] },
          { delay: stagger(0.05), duration: 0.4, easing: "ease-out" }
        )
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../variables';

#main-menu {
  /* No specific styles needed here, using util classes */
}

/* Scroll Area Customization */
.menu-scroll-area {
  overflow-y: auto;
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;

  /* Custom Scrollbar for Chrome/Safari/Opera */
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 4px;

    &:hover {
      background-color: rgba(255, 255, 255, 0.4);
    }
  }
}

/* Logo Stlying */
.logo-wrapper {
  padding: 10px;
  background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%);
  border-radius: 50%;
  box-shadow: 0 0 20px rgba(0,0,0,0.2);
}

.menu-logo {
  height: $logo-height;
  width: auto;
}

.menu-icon-wrapper {
  width: $menu-icon-size;
}

/* Menu Item Styling */
.menu-link {
  border-radius: 12px; /* Smooth rounded corners */
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  border: 1px solid transparent;

  /* HOVER STATE */
  &:hover:not(.active) {
    background: rgba(255, 255, 255, 0.05);
    transform: translateX(4px);

    .menu-text {
      color: #E2E8F0; /* Lighter white */
    }

    .icon-wrapper {
        color: #E2E8F0;
    }
  }

  /* ACTIVE STATE */
  &.active {
    background: rgba(255, 255, 255, 0.1); /* Glassmorphism bg */
    border: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(5px);

    .menu-text {
      color: #fff;
      text-shadow: 0 0 10px rgba(255,255,255,0.3);
    }

    .active-indicator {
      width: 6px;
      height: 6px;
      background-color: #4FD1C5; /* Teal glowing dot */
      border-radius: 50%;
      box-shadow: 0 0 8px #4FD1C5;
    }
  }
}

.text-muted-light {
    color: #94A3B8; /* Slate 400 */
}

.section-title {
    color: #64748B; /* Slate 500 */
}
</style>


